import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, QrCode, Shield, Eye, EyeOff, BookOpen, Linkedin, ExternalLink, Users, CheckCircle } from 'lucide-react';

const ForSpeakers = () => {
  return (
    <div className="bg-navy min-h-screen" data-testid="for-speakers-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <span className="block text-[11px] tracking-[0.3em] uppercase text-gold/60 font-sans font-medium mb-6">For Speakers &amp; Facilitators</span>
            <h1 className="font-serif text-4xl sm:text-5xl text-cream font-light tracking-tight leading-[0.95] mb-6">
              Using the CFQ in your session
            </h1>
            <p className="font-sans text-base sm:text-lg text-cream/40 font-light leading-[1.8] max-w-xl">
              You are welcome to include the Cultural Fluency Quotient in your workshop, retreat, or conference session. Here is what you need to know.
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/15 to-transparent" />
      </section>

      {/* Permission */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="font-serif text-2xl text-cream font-light tracking-tight mb-5">Permission to use</h2>
              <div className="space-y-4 text-cream/40 font-light leading-[1.85] text-base">
                <p>The CFQ is available for use in facilitated sessions at no charge, provided you:</p>
                <div className="space-y-3 pl-1">
                  {[
                    'Credit "Cultural Fluency Quotient (CFQ) by Promise Monday, Third Culture Leadership"',
                    'Do not modify the questions, scoring, or band labels',
                    'Ensure participants give informed consent before starting',
                    'Do not use results for hiring, performance review, or selection',
                    'Do not aggregate or compare individual results without consent',
                  ].map((item, i) => (
                    <div key={i} className="flex gap-3 items-start">
                      <CheckCircle size={14} className="text-gold/50 mt-1 flex-shrink-0" />
                      <span className="text-cream/40 text-base">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-cream font-light tracking-tight mb-5">What you cannot see</h2>
              <div className="space-y-4">
                {[
                  { Icon: EyeOff, title: 'Individual answers', desc: 'The assessment runs in each participant\'s browser. You have no access to their specific responses.' },
                  { Icon: EyeOff, title: 'Individual scores', desc: 'Exact numerical scores are private to the participant. You will not see them.' },
                  { Icon: Eye, title: 'Band labels (with consent)', desc: 'If a participant opts into the anonymous pulse, you can see aggregate band distributions — never tied to a name.' },
                ].map((item, i) => (
                  <div key={i} className="border border-cream/[0.06] bg-white/[0.015] p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <item.Icon size={16} className="text-gold/50" />
                      <h4 className="text-cream/60 text-base font-medium">{item.title}</h4>
                    </div>
                    <p className="text-cream/30 text-sm font-light leading-[1.8]">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QR Code Instructions */}
      <section className="py-16 md:py-20 relative">
        <div className="absolute inset-0 bg-white/[0.01]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <QrCode size={24} className="text-gold/50 mb-4" />
              <h2 className="font-serif text-2xl text-cream font-light tracking-tight mb-5">Workshop QR codes</h2>
              <p className="font-sans text-base text-cream/40 font-light leading-[1.85] mb-5">
                Create a unique session code for your workshop (e.g. your event name or date). Participants scan a QR code that includes your session code in the URL.
              </p>
              <div className="bg-navy-deep border border-cream/[0.06] rounded-lg p-5 mb-5">
                <p className="text-cream/20 text-xs uppercase tracking-wider mb-2">Example URL</p>
                <code className="text-gold text-sm font-mono break-all">thirdcultureleadership.com/cfq-assessment?session=YOUR-CODE</code>
              </div>
              <p className="text-cream/30 text-sm font-light leading-[1.8]">
                Generate a QR code for this URL using any free QR tool. The session code groups anonymous pulse data so you can see aggregate band distributions for your event — without seeing any individual results.
              </p>
            </div>

            <div>
              <h3 className="font-serif text-xl text-cream font-light tracking-tight mb-5">How it works in practice</h3>
              <div className="space-y-4">
                {[
                  { step: '1', text: 'Choose a session code (e.g. "nhs-retreat-oct26")' },
                  { step: '2', text: 'Generate a QR code pointing to /cfq-assessment?session=your-code' },
                  { step: '3', text: 'Participants scan, read the consent screen, and complete the 20 questions on their own device' },
                  { step: '4', text: 'Results appear instantly — private to each participant' },
                  { step: '5', text: 'After results, participants may opt in to share anonymous band data for your session' },
                  { step: '6', text: 'You (the facilitator) see aggregate band distributions, never individual scores' },
                ].map((item) => (
                  <div key={item.step} className="flex gap-4 items-start">
                    <span className="font-serif text-lg text-gold/30 font-light mt-0.5 w-6 flex-shrink-0">{item.step}</span>
                    <p className="text-cream/40 text-base font-light leading-[1.7]">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Resources */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-serif text-2xl text-cream font-light tracking-tight mb-8">Resources</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <Link to="/books" className="group border border-cream/[0.06] bg-white/[0.015] p-6 hover:border-gold/25 transition-all">
              <BookOpen size={18} className="text-gold/40 mb-4 group-hover:text-gold transition-colors" />
              <h3 className="text-cream font-medium text-base mb-2">The Book</h3>
              <p className="text-cream/30 text-sm font-light leading-[1.7]">Cultural Fluency Without Compromise — the full framework behind the CFQ.</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm text-gold/40 group-hover:text-gold transition-colors">Read more <ArrowRight size={12} /></span>
            </Link>

            <a href="https://www.linkedin.com/in/promisemonday" target="_blank" rel="noopener noreferrer" className="group border border-cream/[0.06] bg-white/[0.015] p-6 hover:border-gold/25 transition-all">
              <Linkedin size={18} className="text-gold/40 mb-4 group-hover:text-gold transition-colors" />
              <h3 className="text-cream font-medium text-base mb-2">Connect on LinkedIn</h3>
              <p className="text-cream/30 text-sm font-light leading-[1.7]">Promise Monday — author, speaker, and cultural fluency strategist.</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm text-gold/40 group-hover:text-gold transition-colors">Visit <ExternalLink size={12} /></span>
            </a>

            <Link to="/organisations" className="group border border-cream/[0.06] bg-white/[0.015] p-6 hover:border-gold/25 transition-all">
              <Users size={18} className="text-gold/40 mb-4 group-hover:text-gold transition-colors" />
              <h3 className="text-cream font-medium text-base mb-2">Facilitated Workshops</h3>
              <p className="text-cream/30 text-sm font-light leading-[1.7]">Want a deeper engagement? Enquire about a facilitated session with the author.</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm text-gold/40 group-hover:text-gold transition-colors">Enquire <ArrowRight size={12} /></span>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 border-t border-cream/[0.04]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <p className="font-sans text-base text-cream/30 font-light mb-4">Ready to try the assessment yourself?</p>
          <Link to="/cfq-assessment" className="border border-gold text-gold px-7 py-3.5 text-sm uppercase tracking-[0.12em] font-sans font-medium inline-flex items-center gap-2.5 hover:bg-gold hover:text-navy transition-all duration-300">
            Begin CFQ <ArrowRight size={15} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ForSpeakers;
